import {
	createContext,
	FC,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

interface IStylesStackItem {
	title?: string;
	style: Style;
}

interface IStatusBarStyleSyncContext {
	push: (item: IStylesStackItem) => void;
	pop: () => void;
	replace: (item: IStylesStackItem) => void;
}

const platform = Capacitor.getPlatform();

const StackContext = createContext<IStatusBarStyleSyncContext | undefined>(
	undefined
);

/**
 * StatusBarStyleSyncProvider is a React context provider for managing
 * and synchronizing the style of the StatusBar on mobile platforms.
 *
 * Key Features:
 * - Maintains a stack of styles for the StatusBar.
 * - Provides `push`, `pop`, and `replace` functions to manage the stack.
 * - Automatically applies the style of the top-most stack item to the StatusBar on changes.
 *
 * Usage example:
 * ```tsx
 * const { push, pop } = useStatusBarStyleSync();
 *
 * useEffect(() => {
 *   push({ title: "Home Screen", style: Style.Light });
 *   return () => pop();
 * }, []);
 * ```
 */
export const StatusBarStyleSyncProvider: FC<PropsWithChildren> = ({
	children,
}) => {
	const [stylesStack, setStylesStack] = useState<IStylesStackItem[]>([]);

	const push = useCallback((item: IStylesStackItem) => {
		setStylesStack((prev) => [...prev, item]);
	}, []);

	const pop = useCallback(() => {
		setStylesStack((prev) => prev.slice(0, -1));
	}, []);

	const replace = useCallback((item: IStylesStackItem) => {
		setStylesStack((prev) => [...prev.slice(0, -1), item]);
	}, []);

	/** Sync the StatusBar style to the current background color */
	useEffect(() => {
		const stackTop = stylesStack[stylesStack.length - 1];

		if (platform === 'web' || !stackTop) return;

		StatusBar?.setStyle({ style: stackTop.style });
	}, [stylesStack]);

	const ctx = useMemo<IStatusBarStyleSyncContext>(
		() => ({ push, pop, replace }),
		[push, pop, replace]
	);

	return <StackContext.Provider value={ctx}>{children}</StackContext.Provider>;
};

export const useStatusBarStyleSync = (): IStatusBarStyleSyncContext => {
	const context = useContext(StackContext);
	if (!context) {
		throw new Error(
			'useStatusBarStyleSync must be used within a StatusBarStyleSyncProvider'
		);
	}
	return context;
};
